/**
 * AIPredictions Component
 * Shows ML predictions and indicator-based analysis for symbols across asset classes.
 * Fetches from /api/ml/symbol-prediction/{symbol} with fallback to daily indicator data.
 */
import { useState, useEffect, useCallback } from 'react'
import { Brain, TrendingUp, TrendingDown, AlertCircle, Loader, RefreshCw, Search, BarChart3, Activity } from 'lucide-react'

const API_BASE = import.meta.env.VITE_API_URL || 'https://trading-api-1075463475276.us-central1.run.app';

const ASSET_CLASSES = [
  { id: 'stocks', name: 'Stocks', color: '#22c55e' },
  { id: 'crypto', name: 'Crypto', color: '#f59e0b' },
  { id: 'etfs', name: 'ETFs', color: '#3b82f6' },
  { id: 'forex', name: 'Forex', color: '#a855f7' },
  { id: 'commodities', name: 'Commodities', color: '#f97316' },
  { id: 'indices', name: 'Indices', color: '#ec4899' },
];

// Mini sparkline for accuracy history
function MiniSparkline({ data, color = '#10b981', width = 100, height = 24 }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}

// Confidence histogram component
function ConfidenceHistogram({ predictions }) {
  const bins = [
    { label: '30-50%', min: 30, max: 50, color: '#ef4444' },
    { label: '50-60%', min: 50, max: 60, color: '#f59e0b' },
    { label: '60-70%', min: 60, max: 70, color: '#eab308' },
    { label: '70-80%', min: 70, max: 80, color: '#22c55e' },
    { label: '80-95%', min: 80, max: 95, color: '#10b981' },
  ];
  const counts = bins.map(b => predictions.filter(p => p.confidence >= b.min && p.confidence < (b.max === 95 ? 100 : b.max)).length);
  const maxCount = Math.max(...counts, 1);

  return (
    <div style={{ background: '#0f172a', borderRadius: '10px', padding: '14px', border: '1px solid #1e293b' }}>
      <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <BarChart3 size={14} /> Confidence Distribution
      </div>
      <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-end', height: '60px' }}>
        {bins.map((b, i) => (
          <div key={b.label} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              background: b.color, borderRadius: '3px 3px 0 0',
              height: `${Math.max(4, (counts[i] / maxCount) * 50)}px`,
              transition: 'height 0.3s',
            }} />
            <div style={{ color: '#64748b', fontSize: '9px', marginTop: '4px' }}>{b.label}</div>
            <div style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 700 }}>{counts[i]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AIPredictions({ symbol: initialSymbol = 'SPY', timeframe = 'daily' }) {
  const [loading, setLoading] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [error, setError] = useState(null);
  const [selectedAssetClass, setSelectedAssetClass] = useState('stocks');
  const [searchInput, setSearchInput] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [mlPrediction, setMlPrediction] = useState(null);
  const [predictionHistory, setPredictionHistory] = useState([]);

  // Fetch predictions for top symbols in the selected asset class
  const fetchPredictions = useCallback(async (assetClass) => {
    setLoading(true);
    setError(null);

    try {
      // Get top symbols from daily data
      const res = await fetch(`${API_BASE}/api/twelvedata/${assetClass}/daily?limit=100`);
      const data = await res.json();

      if (!data.success || !data.data || data.data.length === 0) {
        setError(`No data available for ${assetClass}`);
        setPredictions([]);
        setLoading(false);
        return;
      }

      // Sort by growth score (desc), take top 20
      const top = data.data
        .sort((a, b) => (b.growth_score || 0) - (a.growth_score || 0))
        .slice(0, 20);

      // Generate predictions from indicator data
      const preds = top.map(item => {
        const gs = item.growth_score || 0;
        const rsi = item.rsi || item.rsi_14 || 50;
        const macd = item.macd_histogram || item.macd_hist || 0;
        const ema12 = item.ema_12 || 0;
        const ema26 = item.ema_26 || 0;
        const close = item.close || 0;
        const sma200 = item.sma_200 || 0;
        const volume = item.volume || 0;
        const prevClose = item.prev_close || item.open || close;
        const dayChange = prevClose > 0 ? ((close - prevClose) / prevClose * 100) : 0;

        // Compute direction & confidence from indicators
        let bullScore = 0;
        let factors = [];

        if (gs >= 75) { bullScore += 30; factors.push('High Growth Score'); }
        else if (gs >= 50) { bullScore += 15; factors.push('Good Growth Score'); }

        if (rsi >= 40 && rsi <= 65) { bullScore += 15; factors.push('RSI Sweet Spot'); }
        else if (rsi < 30) { bullScore += 10; factors.push('RSI Oversold (bounce)'); }
        else if (rsi > 70) { bullScore -= 10; factors.push('RSI Overbought'); }

        if (macd > 0) { bullScore += 15; factors.push('MACD Bullish'); }
        else if (macd < 0) { bullScore -= 10; factors.push('MACD Bearish'); }

        if (ema12 > ema26) { bullScore += 15; factors.push('EMA Rise Cycle'); }
        else if (ema12 < ema26) { bullScore -= 10; factors.push('EMA Fall Cycle'); }

        if (close > sma200 && sma200 > 0) { bullScore += 10; factors.push('Above SMA 200'); }

        if (item.pivot_low_flag === 1) { bullScore += 10; factors.push('Pivot Low Signal'); }
        if (item.pivot_high_flag === 1) { bullScore -= 5; factors.push('Pivot High Warning'); }

        const direction = bullScore >= 15 ? 'UP' : bullScore <= -5 ? 'DOWN' : 'NEUTRAL';
        const confidence = Math.min(Math.max(Math.abs(bullScore) + 40, 30), 95);
        const recommendation = bullScore >= 40 ? 'STRONG_BUY'
          : bullScore >= 20 ? 'BUY'
          : bullScore >= 5 ? 'HOLD'
          : bullScore >= -10 ? 'SELL'
          : 'STRONG_SELL';

        return {
          symbol: item.symbol,
          close,
          dayChange,
          volume,
          growthScore: gs,
          rsi,
          macd,
          emaRise: ema12 > ema26,
          direction,
          confidence,
          recommendation,
          factors,
          datetime: item.datetime,
        };
      });

      setPredictions(preds);
      if (preds.length > 0 && !selectedSymbol) {
        setSelectedSymbol(preds[0].symbol);
      }
    } catch (err) {
      setError('Failed to load predictions: ' + err.message);
    }

    setLoading(false);
  }, [selectedSymbol]);

  useEffect(() => {
    fetchPredictions(selectedAssetClass);
  }, [selectedAssetClass]);

  // Fetch ML prediction for selected symbol
  const fetchMLPrediction = useCallback(async (sym) => {
    if (!sym) return;
    try {
      const [predRes, histRes] = await Promise.allSettled([
        fetch(`${API_BASE}/api/ml/symbol-prediction/${sym}`).then(r => r.json()),
        fetch(`${API_BASE}/api/ml/symbol-prediction/${sym}/history`).then(r => r.json())
      ]);
      if (predRes.status === 'fulfilled' && predRes.value.prediction) {
        setMlPrediction(predRes.value);
      } else {
        setMlPrediction(null);
      }
      if (histRes.status === 'fulfilled' && histRes.value.history) {
        setPredictionHistory(histRes.value.history);
      } else {
        setPredictionHistory([]);
      }
    } catch { setMlPrediction(null); setPredictionHistory([]); }
  }, []);

  useEffect(() => {
    if (selectedSymbol) fetchMLPrediction(selectedSymbol);
  }, [selectedSymbol, fetchMLPrediction]);

  const handleAssetClassChange = (ac) => {
    setSelectedAssetClass(ac);
    setSelectedSymbol(null);
    setMlPrediction(null);
    setPredictionHistory([]);
    setSearchInput('');
  };

  const getRecStyle = (rec) => {
    const map = {
      STRONG_BUY: { bg: '#10b98122', color: '#10b981', label: 'Strong Buy' },
      BUY: { bg: '#34d39922', color: '#34d399', label: 'Buy' },
      HOLD: { bg: '#f59e0b22', color: '#f59e0b', label: 'Hold' },
      SELL: { bg: '#f8717122', color: '#f87171', label: 'Sell' },
      STRONG_SELL: { bg: '#ef444422', color: '#ef4444', label: 'Strong Sell' },
    };
    return map[rec] || { bg: '#6b728022', color: '#6b7280', label: rec };
  };

  const filtered = searchInput
    ? predictions.filter(p => p.symbol.toLowerCase().includes(searchInput.toLowerCase()))
    : predictions;

  const detail = predictions.find(p => p.symbol === selectedSymbol);

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ color: 'white', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Brain size={28} style={{ color: '#10b981' }} />
            AI Predictions
          </h2>
          <p style={{ color: '#64748b', margin: 0, fontSize: '14px' }}>
            ML-powered direction predictions from growth score, RSI, MACD, EMA cycles & pivot signals
          </p>
        </div>
        <button onClick={() => fetchPredictions(selectedAssetClass)} disabled={loading}
          style={{
            background: '#1e293b', border: '1px solid #334155', borderRadius: '8px',
            color: '#94a3b8', padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px'
          }}>
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Asset Class Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {ASSET_CLASSES.map(ac => (
          <button key={ac.id} onClick={() => handleAssetClassChange(ac.id)}
            style={{
              background: selectedAssetClass === ac.id ? ac.color : 'transparent',
              color: selectedAssetClass === ac.id ? 'white' : '#94a3b8',
              border: `1px solid ${selectedAssetClass === ac.id ? ac.color : '#334155'}`,
              borderRadius: '8px', padding: '8px 16px', cursor: 'pointer',
              fontSize: '13px', fontWeight: selectedAssetClass === ac.id ? 700 : 500,
            }}>
            {ac.name}
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: '16px', position: 'relative', maxWidth: '300px' }}>
        <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
        <input
          placeholder="Filter symbols..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          style={{
            width: '100%', padding: '10px 12px 10px 36px', background: '#0f172a',
            border: '1px solid #334155', borderRadius: '8px', color: 'white', fontSize: '14px'
          }}
        />
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
          <Loader size={32} className="animate-spin" style={{ marginBottom: '12px' }} />
          <div>Loading predictions for {selectedAssetClass}...</div>
        </div>
      )}

      {error && (
        <div style={{ background: '#ef444422', border: '1px solid #ef4444', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
          <AlertCircle size={32} style={{ color: '#ef4444', marginBottom: '8px' }} />
          <div style={{ color: '#ef4444' }}>{error}</div>
        </div>
      )}

      {!loading && !error && (
        <div style={{ display: 'grid', gridTemplateColumns: detail ? '1fr 1fr' : '1fr', gap: '20px' }}>
          {/* Predictions Table */}
          <div style={{
            background: '#1e293b80', borderRadius: '12px', border: '1px solid #334155',
            overflow: 'hidden', maxHeight: '700px', overflowY: 'auto',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#0f172a', position: 'sticky', top: 0, zIndex: 1 }}>
                  <th style={thStyle}>Symbol</th>
                  <th style={thStyle}>Price</th>
                  <th style={thStyle}>GS</th>
                  <th style={thStyle}>Direction</th>
                  <th style={thStyle}>Confidence</th>
                  <th style={thStyle}>Signal</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const rec = getRecStyle(p.recommendation);
                  const isSelected = p.symbol === selectedSymbol;
                  return (
                    <tr key={p.symbol}
                      onClick={() => setSelectedSymbol(p.symbol)}
                      style={{
                        cursor: 'pointer', borderBottom: '1px solid #1e293b',
                        background: isSelected ? '#334155' : 'transparent',
                      }}>
                      <td style={{ ...tdStyle, fontWeight: 700, color: 'white' }}>{p.symbol}</td>
                      <td style={tdStyle}>
                        ${Number(p.close).toFixed(2)}
                        <span style={{ color: p.dayChange >= 0 ? '#10b981' : '#ef4444', fontSize: '11px', marginLeft: '4px' }}>
                          {p.dayChange >= 0 ? '+' : ''}{p.dayChange.toFixed(1)}%
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          color: p.growthScore >= 75 ? '#10b981' : p.growthScore >= 50 ? '#f59e0b' : '#ef4444',
                          fontWeight: 700,
                        }}>{p.growthScore}</span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          color: p.direction === 'UP' ? '#10b981' : p.direction === 'DOWN' ? '#ef4444' : '#f59e0b',
                        }}>
                          {p.direction === 'UP' ? <TrendingUp size={14} /> : p.direction === 'DOWN' ? <TrendingDown size={14} /> : '—'}
                          {p.direction}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{
                            width: '40px', height: '6px', background: '#0f172a', borderRadius: '3px', overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${p.confidence}%`, height: '100%', borderRadius: '3px',
                              background: p.confidence >= 70 ? '#10b981' : p.confidence >= 50 ? '#f59e0b' : '#ef4444',
                            }} />
                          </div>
                          <span style={{ fontSize: '12px', color: '#94a3b8' }}>{p.confidence}%</span>
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          background: rec.bg, color: rec.color, padding: '3px 10px',
                          borderRadius: '6px', fontSize: '11px', fontWeight: 700,
                        }}>{rec.label}</span>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#64748b' }}>
                    No symbols found
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Detail Panel */}
          {detail && (
            <div style={{ background: '#1e293b80', borderRadius: '12px', border: '1px solid #334155', padding: '24px' }}>
              <h3 style={{ color: 'white', margin: '0 0 20px', fontSize: '20px' }}>
                {detail.symbol} — Detailed Analysis
              </h3>

              {/* Direction + Confidence Hero */}
              <div style={{
                background: detail.direction === 'UP' ? '#10b98118' : detail.direction === 'DOWN' ? '#ef444418' : '#f59e0b18',
                border: `2px solid ${detail.direction === 'UP' ? '#10b981' : detail.direction === 'DOWN' ? '#ef4444' : '#f59e0b'}`,
                borderRadius: '12px', padding: '20px', marginBottom: '20px', textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '36px', fontWeight: 800,
                  color: detail.direction === 'UP' ? '#10b981' : detail.direction === 'DOWN' ? '#ef4444' : '#f59e0b',
                }}>
                  {detail.direction === 'UP' ? '↗ BULLISH' : detail.direction === 'DOWN' ? '↘ BEARISH' : '→ NEUTRAL'}
                </div>
                <div style={{ color: '#94a3b8', fontSize: '14px', marginTop: '8px' }}>
                  Confidence: <strong style={{ color: 'white' }}>{detail.confidence}%</strong>
                </div>
                <div style={{ marginTop: '8px' }}>
                  {(() => { const r = getRecStyle(detail.recommendation); return (
                    <span style={{ background: r.bg, color: r.color, padding: '6px 20px', borderRadius: '8px', fontWeight: 700, fontSize: '16px' }}>
                      {r.label}
                    </span>
                  );})()}
                </div>
              </div>

              {/* Indicators Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px' }}>
                <MetricCard label="Price" value={`$${Number(detail.close).toFixed(2)}`} sub={`${detail.dayChange >= 0 ? '+' : ''}${detail.dayChange.toFixed(2)}%`} subColor={detail.dayChange >= 0 ? '#10b981' : '#ef4444'} />
                <MetricCard label="Growth Score" value={detail.growthScore} sub={detail.growthScore >= 75 ? 'Excellent' : detail.growthScore >= 50 ? 'Good' : 'Low'} subColor={detail.growthScore >= 75 ? '#10b981' : detail.growthScore >= 50 ? '#f59e0b' : '#ef4444'} />
                <MetricCard label="RSI (14)" value={detail.rsi.toFixed(1)} sub={detail.rsi < 30 ? 'Oversold' : detail.rsi > 70 ? 'Overbought' : 'Normal'} subColor={detail.rsi < 30 ? '#10b981' : detail.rsi > 70 ? '#ef4444' : '#94a3b8'} />
                <MetricCard label="MACD Histogram" value={detail.macd.toFixed(4)} sub={detail.macd > 0 ? 'Bullish' : 'Bearish'} subColor={detail.macd > 0 ? '#10b981' : '#ef4444'} />
                <MetricCard label="EMA 12/26" value={detail.emaRise ? 'Rise Cycle' : 'Fall Cycle'} sub={detail.emaRise ? 'Bullish crossover' : 'Bearish crossover'} subColor={detail.emaRise ? '#10b981' : '#ef4444'} />
                <MetricCard label="Volume" value={detail.volume > 1e6 ? `${(detail.volume / 1e6).toFixed(1)}M` : detail.volume > 1e3 ? `${(detail.volume / 1e3).toFixed(0)}K` : detail.volume.toString()} sub="24h Volume" subColor="#94a3b8" />
              </div>

              {/* ML vs Indicator Comparison */}
              {mlPrediction && mlPrediction.prediction && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#a855f7', margin: '0 0 12px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Activity size={14} /> ML Model vs Indicator Analysis
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div style={{ background: '#10b98115', border: '1px solid #10b98140', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ color: '#64748b', fontSize: '11px', marginBottom: '4px' }}>ML Model</div>
                      <div style={{ color: (mlPrediction.prediction.direction || '').toUpperCase() === 'UP' ? '#10b981' : '#ef4444', fontSize: '18px', fontWeight: 700 }}>
                        {(mlPrediction.prediction.direction || 'N/A').toUpperCase()}
                      </div>
                      <div style={{ color: '#94a3b8', fontSize: '12px' }}>
                        {(mlPrediction.prediction.confidence * 100 || 0).toFixed(0)}% conf
                      </div>
                    </div>
                    <div style={{ background: '#3b82f615', border: '1px solid #3b82f640', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ color: '#64748b', fontSize: '11px', marginBottom: '4px' }}>Indicators</div>
                      <div style={{ color: detail.direction === 'UP' ? '#10b981' : detail.direction === 'DOWN' ? '#ef4444' : '#f59e0b', fontSize: '18px', fontWeight: 700 }}>
                        {detail.direction}
                      </div>
                      <div style={{ color: '#94a3b8', fontSize: '12px' }}>{detail.confidence}% conf</div>
                    </div>
                  </div>
                </div>
              )}

              {/* 30-Day Accuracy Tracker */}
              {predictionHistory.length > 0 && (
                <div style={{ background: '#0f172a', borderRadius: '10px', padding: '14px', border: '1px solid #1e293b', marginBottom: '20px' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Activity size={14} /> 30-Day Rolling Accuracy
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <MiniSparkline
                      data={predictionHistory.map(h => h.accuracy || h.rolling_accuracy || 50)}
                      color="#a855f7"
                      width={140}
                      height={28}
                    />
                    <span style={{ color: 'white', fontSize: '16px', fontWeight: 700 }}>
                      {(predictionHistory[predictionHistory.length - 1]?.accuracy || predictionHistory[predictionHistory.length - 1]?.rolling_accuracy || 0).toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}

              {/* Confidence Histogram */}
              <ConfidenceHistogram predictions={predictions} />

              {/* Key Factors */}
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ color: '#10b981', margin: '0 0 12px', fontSize: '14px' }}>Analysis Factors</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {detail.factors.map((f, i) => {
                    const isBull = f.includes('Rise') || f.includes('Bullish') || f.includes('Sweet') || f.includes('High') || f.includes('Good') || f.includes('Above') || f.includes('Pivot Low') || f.includes('Oversold');
                    return (
                      <span key={i} style={{
                        background: isBull ? '#10b98122' : '#ef444422',
                        color: isBull ? '#10b981' : '#ef4444',
                        padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                      }}>{f}</span>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, sub, subColor }) {
  return (
    <div style={{ background: '#0f172a', borderRadius: '10px', padding: '14px', border: '1px solid #1e293b' }}>
      <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '6px' }}>{label}</div>
      <div style={{ color: 'white', fontSize: '20px', fontWeight: 700 }}>{value}</div>
      {sub && <div style={{ color: subColor || '#94a3b8', fontSize: '12px', marginTop: '2px' }}>{sub}</div>}
    </div>
  );
}

const thStyle = { padding: '10px 12px', textAlign: 'left', color: '#64748b', fontSize: '12px', fontWeight: 600, borderBottom: '2px solid #334155' };
const tdStyle = { padding: '10px 12px', color: '#94a3b8', fontSize: '13px' };

export default AIPredictions
